import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";

/**
 * Phase 14.9.9 — Clarify-action card (soft date / month gate).
 *
 * Surfaced when the user asked for an itinerary without specifying a
 * travel date / month. Renders 12 month chips and, on click, sends a
 * follow-up chat message built from the backend-provided
 * `rephrase_template` (e.g. "5 day itinerary for Goa from Delhi in
 * Aug 2026"). The follow-up re-enters the chat flow with an explicit
 * month so the cost-summary card re-prices for that window.
 */

export default function ClarifyActionCard({ card, onPick }) {
    const { t } = useTranslation();
    const chips = card.chips || [];
    const template = card.rephrase_template || "";

    const handlePick = (chip) => {
        if (!onPick) return;
        const followUp = template
            ? template.replace("{month}", chip.label)
            : `In ${chip.label}`;
        onPick(followUp);
    };

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="clarify-action-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#F97316] ring-4 ring-[#F97316]/10"
            />
            <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F5F5]">
                    <CalendarDays size={13} strokeWidth={2} className="text-[#0A0A0A]" />
                </span>
                <span className="text-[12px] font-medium uppercase tracking-wider text-[#737373]">
                    {card.prompt ? "Pick a month" : "When are you traveling?"}
                </span>
            </div>
            {card.prompt && (
                <p
                    className="mt-2 text-[13.5px] text-[#0A0A0A]"
                    data-testid="clarify-prompt"
                >
                    {card.prompt}
                </p>
            )}
            {card.best_month_hint && (
                <p
                    className="mt-1 text-[12px] text-[#737373]"
                    data-testid="clarify-best-month"
                >
                    Best season for {card.destination}: <span className="font-medium text-[#0A0A0A]">{card.best_month_hint}</span>
                </p>
            )}
            <div
                className="mt-4 flex flex-wrap gap-2"
                data-testid="clarify-chip-row"
            >
                {chips.map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        onClick={() => handlePick(chip)}
                        data-testid={`clarify-chip-${chip.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F97316]" />
                        {chip.label}
                    </button>
                ))}
            </div>
        </article>
    );
}
